import { create } from 'zustand';

/**
 * Composer bridge — the one channel through which another part of the chat
 * panel puts text into the composer draft (spec F3 / T3-F: the customer-
 * profile sidebar's 「插入」 buttons).
 *
 * INSERT ONLY, NEVER SEND. A request lands in the draft at the caret; the
 * agent reviews it and presses send themselves. Nothing here can send, and
 * the sidebar has no other path to the send API.
 *
 * Protocol:
 *   - the producer calls `requestInsert(conversationId, text)`;
 *   - the mounted `Composer` subscribes to `requests` and consumes each one
 *     exactly once (`consume(id)`): a request for its own `conversationId`
 *     is spliced in at the caret (`insertAtCaret`) and the textarea focused;
 *     a request for any other conversation is dropped with a toast (the
 *     composer now belongs to a different conversation);
 *   - a draft-scope reset (conversation switch, mount) consumes whatever is
 *     still pending FIRST, so a stale request can never land in another
 *     conversation's draft.
 *
 * Single consumer by design: the console mounts at most one Composer (the
 * single-conversation panel's or the merged thread's), and the sidebar keys
 * on exactly the conversation that Composer targets (ChatPanel /
 * ThreadPanel) — the conversation id check is the safety net, not routing.
 */
export interface ComposerInsertRequest {
  /** Monotonic and never reused — what `consume` removes. */
  id: number;
  /** The conversation whose composer the text is meant for. */
  conversationId: string;
  /** Inserted verbatim (see `insertAtCaret` for the separators around it). */
  text: string;
}

interface ComposerBridgeState {
  /** Unconsumed requests, oldest first. */
  requests: ComposerInsertRequest[];
  /** Queue `text` for the composer of `conversationId`. Empty text is ignored. */
  requestInsert: (conversationId: string, text: string) => void;
  /** Drop request `id` — a no-op when it is already gone. */
  consume: (id: number) => void;
}

let nextRequestId = 1;

export const useComposerBridge = create<ComposerBridgeState>((set) => ({
  requests: [],
  requestInsert: (conversationId, text) => {
    if (text === '') return;
    const request: ComposerInsertRequest = { id: nextRequestId, conversationId, text };
    nextRequestId += 1;
    set((state) => ({ requests: [...state.requests, request] }));
  },
  consume: (id) =>
    set((state) =>
      state.requests.some((request) => request.id === id)
        ? { requests: state.requests.filter((request) => request.id !== id) }
        : state,
    ),
}));

/**
 * Splice `text` into `draft` at `caret` WITHOUT removing or changing any
 * existing character — an in-progress draft is only ever extended.
 *
 * Separators (spec 03 §3.1), chosen by the INSERT's shape — a newline for a
 * multi-line insert, a space for a single-line one:
 *   - before it: none when the text before the caret is empty or ends in
 *     whitespace, else one separator;
 *   - after it (only matters mid-draft): the mirror rule — none when the text
 *     after the caret is empty or starts with whitespace, else one
 *     separator, so an inserted value never fuses with the next word.
 *
 * Returns the new draft and the caret index right after the inserted text.
 */
export function insertAtCaret(
  draft: string,
  caret: number,
  text: string,
): { value: string; caret: number } {
  const at = Math.min(Math.max(caret, 0), draft.length);
  const before = draft.slice(0, at);
  const after = draft.slice(at);
  const separator = text.includes('\n') ? '\n' : ' ';
  const lead = before === '' || /\s$/.test(before) ? '' : separator;
  const trail = after === '' || /^\s/.test(after) ? '' : separator;
  return {
    value: before + lead + text + trail + after,
    caret: before.length + lead.length + text.length,
  };
}
